// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.MenuContext.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * MenuContext Component
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Portal from './Izenda.Portal';
import AppGlobal from 'Izenda.AppGlobal';
import { isEqual, isNumber, getOr } from 'lodash/fp';

const EVENT_PREFIX = 'Izenda-MenuContext_';
const ZERO = 0;

export default class MenuContext extends Component {
  static propTypes = {
    isDisabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    children: PropTypes.element.isRequired,
    menuComponent: PropTypes.element,
    onMenuContextChanged: PropTypes.func
  };

  customEventName: string;
  mountNode: any;

  constructor(props: any) {
    super(props);

    this.state = {
      left: 0,
      top: 0,
      isShowMenuContext: false,
      isDisabled: props.isDisabled || false
    };
    this.customEventName = EVENT_PREFIX + props.id;
  }

  componentDidMount() {
    document.addEventListener(this.customEventName, this.onShowMenuContextEvent, false);
    this.initListener();
  }

  shouldComponentUpdate(nextProps: any, nextState: any): any {
    return (
      nextState.isShowMenuContext !== this.state.isShowMenuContext ||
      nextState.top !== this.state.top ||
      nextState.left !== this.state.left ||
      !isEqual(nextProps, this.props)
    );
  }

  componentWillUnmount() {
    document.removeEventListener(this.customEventName, this.onShowMenuContextEvent);
    this.destroyListener();
  }

  initListener = () => {
    document.addEventListener('click', this.onClickOverMenu);
    document.addEventListener('mousedown', this.onClickOverMenu);
    document.addEventListener('mousewheel', this.onClickOverMenu);
    window.addEventListener('resize', this.onClickOverMenu);
  };

  destroyListener = () => {
    document.removeEventListener('click', this.onClickOverMenu);
    document.removeEventListener('mousedown', this.onClickOverMenu);
    document.removeEventListener('mousewheel', this.onClickOverMenu);
    window.removeEventListener('resize', this.onClickOverMenu);
  };

  getMousePosition = (event: any) => {
    const element = AppGlobal.containerElement.getBoundingClientRect();
    const xPos = event.clientX || getOr(ZERO, 'touches[0].pageX', event);
    const yPos = event.clientY || getOr(ZERO, 'touches[0].pageY', event);
    const xOffset = Math.max(document.documentElement.scrollLeft, document.body.scrollLeft);
    const yOffset = Math.max(document.documentElement.scrollTop, document.body.scrollTop);

    return {
      left: xPos + xOffset - element.left,
      top: yPos + yOffset - element.top
    };
  };

  show = (position: any) => {
    if (this.props.isDisabled) {
      return;
    }

    this.setState({
      isShowMenuContext: true,
      top: position.top,
      left: position.left
    });

    this.props.onMenuContextChanged && this.props.onMenuContextChanged(true);
  };

  hide = () => {
    this.props.onMenuContextChanged && this.props.onMenuContextChanged(false);
    this.setState({
      isShowMenuContext: false
    });
  };

  onClickOverMenu = (event: any) => {
    const container = this.mountNode;
    function isBelongToMenuCheck(dom) {
      while (dom != null && dom.parentNode != window) {
        if (dom === container) return true;
        dom = dom.parentNode;
      }
      return false;
    }

    const isBelongToMenu = isBelongToMenuCheck(event.target);

    if (isBelongToMenu && typeof event.target.click === 'function' && event.type === 'click') {
      this.hide();
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    !isBelongToMenu && this.hide();
    return false;
  };

  onShowMenuContextEvent = (event: any) => {
    this.state.isShowMenuContext && event.type !== this.customEventName && this.hide();
    isNumber(event.detail.top) && isNumber(event.detail.left) && this.show(event.detail);
  };

  dispatchEvent = (dataToDispatch: any) => {
    const customEvent = new CustomEvent(this.customEventName, { detail: dataToDispatch });
    document.dispatchEvent(customEvent);
  };

  onContextMenuEvent = (event: any) => {
    const pos = this.getMousePosition(event);
    isNumber(pos.top) && isNumber(pos.left) && this.dispatchEvent(pos);

    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  getPositionStyle = () => {
    const isShowMenuContext = this.state.isShowMenuContext || false;

    return {
      position: 'absolute',
      top: this.state.top,
      left: this.state.left,
      display: isShowMenuContext ? null : 'none',
      zIndex: 1000
    };
  };

  setMountNode = (ref: any) => {
    this.mountNode = ref ? ref.getMountNode() : ref;
  };

  render() {
    return (
      <div className='izenda-MenuContext' onContextMenu={this.onContextMenuEvent}>
        {this.props.children}
        {this.state.isShowMenuContext && (
          <Portal ref={this.setMountNode}>
            <div style={this.getPositionStyle()}>{this.props.menuComponent}</div>
          </Portal>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.MenuContext.js